import { useQueryClient } from '@tanstack/react-query';
import { ContractApiModel } from '../../../../types/models/contract.model';
import { CreateContractApiRequest } from '../../../../types/requests/contracts/create-contract.request';
import { DeleteContractApiRequest } from '../../../../types/requests/contracts/delete-contract.request';
import { GetContractApiRequest } from '../../../../types/requests/contracts/get-contract.request';
import { SearchContractsApiRequest } from '../../../../types/requests/contracts/search-contracts.request';
import { UpdateContractApiRequest } from '../../../../types/requests/contracts/update-contract.request';
import { createContract, deleteContract, getContract, searchContracts, updateContract } from '../../api/contracts.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';

const baseQueryKey = 'contract';
const searchQueryKey = 'search';

export const useCreateContract = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateContractApiRequest) => (await createContract(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, contract: data }] as const;
};

export const useUpdateContract = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateContractApiRequest) => (await updateContract(params)).data,
    onSuccess: (contract) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetContractApiRequest)?.contractId === contract.id),
      }),
  });
  return [request, { ...result, contract: data }] as const;
};

export const useGetContract = <T extends ContractApiModel>(params: GetContractApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getContract<T>(params)).data,
    enabled: Boolean(params.contractId && !Number.isNaN(params.contractId)),
  });
  return { ...result, contract: data } as const;
};

export const useSearchContracts = (params: SearchContractsApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchContracts({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteContract = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteContractApiRequest) => (await deleteContract(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};
